import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import "./attendanceCal.css";
import Layout from "../../../Layout/Layout";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import ItemServiceV2 from "../../../services/human_resource_appCommonService";

const STATUS_META = {
  Present: { label: "Present", dot: "var(--present)" },
  Absent: { label: "Absent", dot: "var(--absent)" },
  Late: { label: "Late", dot: "var(--late)" },
  Leave: { label: "On Leave", dot: "var(--leave)" },
  Holiday: { label: "Holiday", dot: "var(--holiday)" },
};

const WEEKDAYS_FULL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const EMPTY_FORM = { status: "Present", startTime: "", endTime: "", notes: "" };

function toKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isAdminUser(userData) {
  if (!userData) return false;
  const hasAdminRole = userData.roles?.some((r) => r.roleId === "ADMIN");
  const hasAdminJobRole = userData.jobRole === "Admin";
  return Boolean(hasAdminRole || hasAdminJobRole);
}

export default function AttendanceCal({ onSaveEntry }) {
  const today = useMemo(() => new Date(), []);
  const todayStart = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), today.getDate()),
    [today],
  );
  const userData = useSelector((state) => state?.user?.data);

  const isAdmin = useMemo(() => isAdminUser(userData), [userData]);
  const loggedInEmployeeCode = userData?.customerCode || userData?.userId || "";
  const loggedInEmployeeName =
    userData?.employeeName || userData?.organisationName || "Employee";

  const [view, setView] = useState("month"); // "day" | "week" | "month"
  const [cursor, setCursor] = useState(
    new Date(today.getFullYear(), today.getMonth(), today.getDate()),
  );
  const [selected, setSelected] = useState(cursor);
  const [employeeList, setEmployeeList] = useState([]);
  const [selectedEmployeeCode, setSelectedEmployeeCode] = useState("");
  const [entries, setEntries] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const [modalEmployeeCode, setModalEmployeeCode] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const firstFieldRef = useRef(null);
  const [listModalDate, setListModalDate] = useState(null);

  const monthLabel = `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`;

  const headerDateLabel = useMemo(() => {
    const opts = {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    };
    return selected.toLocaleDateString("en-US", opts);
  }, [selected]);
  const monthGrid = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay();
    const gridStart = new Date(year, month, 1 - startOffset);

    const weeks = [];
    let day = new Date(gridStart);
    for (let w = 0; w < 6; w++) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        week.push(new Date(day));
        day.setDate(day.getDate() + 1);
      }
      weeks.push(week);
    }
    return weeks;
  }, [cursor]);

  const weekGrid = useMemo(() => {
    const start = new Date(selected);
    start.setDate(start.getDate() - start.getDay());
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [selected]);

  const miniGrid = monthGrid;

  const isMultiEmployeeView = isAdmin && !selectedEmployeeCode;
  function isPastDay(date) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return d < todayStart;
  }

  function canEditDate(date) {
    return isAdmin || !isPastDay(date);
  }
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      const params = {
        month: cursor.getMonth() + 1,
        year: cursor.getFullYear(),
      };
      await ItemServiceV2.getAllAttendance(params).then((item) => {
        if (item?.status === 200) {
          const all = item?.data || [];
          const uniqueMap = new Map();
          all.forEach((rec) => {
            if (rec.employeeCode && !uniqueMap.has(rec.employeeCode)) {
              uniqueMap.set(
                rec.employeeCode,
                rec.employeeName || rec.employeeCode,
              );
            }
          });
          setEmployeeList(
            Array.from(uniqueMap, ([code, name]) => ({ code, name })),
          );
        } else {
          toast.error(item?.message || "Unable to load employee list", {
            duration: 4000,
            position: "top-right",
          });
        }
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);
  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    setError("");

    const month = cursor.getMonth() + 1;
    const year = cursor.getFullYear();
    const params = { month, year };
    if (isAdmin && selectedEmployeeCode)
      params.employeeCode = selectedEmployeeCode;

    const callApi = isAdmin
      ? ItemServiceV2.getAllAttendance(params)
      : ItemServiceV2.getAttendance(params);

    await callApi
      .then((item) => {
        if (item?.status === 200) {
          const records = item?.data || [];
          const mapped = {};
          records.forEach((rec) => {
            const dateKey = rec.attendanceDate || rec.date;
            if (!dateKey) return;
            if (isAdmin && !selectedEmployeeCode) {
              if (!mapped[dateKey]) mapped[dateKey] = [];
              mapped[dateKey].push(rec);
            } else {
              mapped[dateKey] = rec;
            }
          });
          setEntries(mapped);
        } else {
          setError(item?.message || "Unable to load attendance data");
          toast.error(item?.message || "Unable to load attendance data", {
            duration: 4000,
            position: "top-right",
          });
        }
      })
      .catch(() => {
        setError("Unable to load attendance data");
      });

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, isAdmin, selectedEmployeeCode]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);
  // const stats = useMemo(() => {
  //   const counts = { Present: 0, absent: 0, late: 0, leave: 0, holiday: 0 };
  //   Object.entries(entries).forEach(([key, entry]) => {
  //     const d = new Date(key + "T00:00:00");
  //     if (
  //       d.getMonth() !== cursor.getMonth() ||
  //       d.getFullYear() !== cursor.getFullYear()
  //     )
  //       return;
  //     if (isMultiEmployeeView && Array.isArray(entry)) {
  //       entry.forEach((e) => {
  //         if (counts[e.status] !== undefined) counts[e.status] += 1;
  //       });
  //     } else if (entry?.status && counts[entry.status] !== undefined) {
  //       counts[entry.status] += 1;
  //     }
  //   });
  //   return counts;
  // }, [entries, cursor, isMultiEmployeeView]);
const stats = useMemo(() => {
  const counts = {
    Present: 0,
    Absent: 0,
    Late: 0,
    Leave: 0,
    Holiday: 0,
  };

  Object.entries(entries).forEach(([key, entry]) => {
    const d = new Date(key + "T00:00:00");

    if (
      d.getMonth() !== cursor.getMonth() ||
      d.getFullYear() !== cursor.getFullYear()
    ) {
      return;
    }

    if (isMultiEmployeeView && Array.isArray(entry)) {
      entry.forEach((e) => {
        if (counts[e.status] !== undefined) {
          counts[e.status]++;
        }
      });
    } else if (entry?.status && counts[entry.status] !== undefined) {
      counts[entry.status]++;
    }
  });

  return counts;
}, [entries, cursor, isMultiEmployeeView]);
  function entryFor(date) {
    const dow = date.getDay();
    if (dow === 0 || dow === 6) return null;
    return entries[toKey(date)] || null;
  }

  function goToday() {
    const t = new Date();
    setCursor(new Date(t.getFullYear(), t.getMonth(), t.getDate()));
    setSelected(t);
  }

  function stepMonth(delta) {
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));
  }

  function stepDay(delta) {
    const d = new Date(selected);
    d.setDate(d.getDate() + delta);
    setSelected(d);
    setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
  }

  function stepWeek(delta) {
    const d = new Date(selected);
    d.setDate(d.getDate() + delta * 7);
    setSelected(d);
    setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
  }

  function handlePrev() {
    if (view === "month") stepMonth(-1);
    else if (view === "week") stepWeek(-1);
    else stepDay(-1);
  }

  function handleNext() {
    if (view === "month") stepMonth(1);
    else if (view === "week") stepWeek(1);
    else stepDay(1);
  }
  function openListModal(date) {
    setSelected(date);
    setListModalDate(date);
  }

  function closeListModal() {
    setListModalDate(null);
  }

  function openEditFromList(date, employeeCode) {
    setListModalDate(null);
    openModalFor(date, employeeCode);
  }
  function openModalFor(date, employeeCodeOverride) {
    if (isMultiEmployeeView && !employeeCodeOverride) {
      openListModal(date);
      return;
    }

    if (!canEditDate(date)) {
      toast.error(
        "Only an admin can add or edit attendance for a previous day",
        {
          duration: 3500,
          position: "top-right",
        },
      );
      setSelected(date);
      return;
    }

    const targetEmployeeCode =
      employeeCodeOverride ||
      (isAdmin && selectedEmployeeCode
        ? selectedEmployeeCode
        : loggedInEmployeeCode);

    setSelected(date);
    setModalEmployeeCode(targetEmployeeCode);

    let existing = entries[toKey(date)];
    if (Array.isArray(existing)) {
      existing =
        existing.find((e) => e.employeeCode === targetEmployeeCode) || null;
    }

    setForm(
      existing
        ? {
            status: existing.status || "Present",
            startTime: existing.startTime || "",
            endTime: existing.endTime || "",
            notes: existing.notes || "",
          }
        : { ...EMPTY_FORM },
    );
    setModalDate(date);
  }

  function closeModal() {
    setModalDate(null);
    setModalEmployeeCode("");
  }

  const handleSave = async (e) => {
    e.preventDefault();
    if (!modalDate) return;

    const key = toKey(modalDate);
    const employeeCode = modalEmployeeCode || loggedInEmployeeCode;

      const employeeName = isAdmin
    ? employeeList.find((emp) => emp.code === employeeCode)?.name ||
      employeeCode
    : loggedInEmployeeName;


    const obj = {
      data: {
        ...form,
        attendanceDate: key,
        attendanceMonth: modalDate.getMonth() + 1,
        attendanceYear: modalDate.getFullYear(),
        employeeCode,
         employeeName,
      },
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    setDisabled(true);
    await ItemServiceV2.markAttendance(obj).then((item) => {
      if (item?.status === 200) {
        toast.success(item?.message || "Attendance saved successfully", {
          duration: 2000,
          position: "top-right",
        });

        const employeeName = isAdmin
          ? employeeList.find((emp) => emp.code === employeeCode)?.name ||
            employeeCode
          : loggedInEmployeeName;

        const newRecord = {
          ...form,
          attendanceDate: key,
          employeeCode,
          employeeName,
        };
        setEntries((prev) => {
          const updated = { ...prev };
          const existing = updated[key];
          if (Array.isArray(existing)) {
            const list = [...existing];
            const idx = list.findIndex((r) => r.employeeCode === employeeCode);
            if (idx >= 0) list[idx] = newRecord;
            else list.push(newRecord);
            updated[key] = list;
          } else {
            updated[key] = newRecord;
          }
          return updated;
        });
        if (isAdmin) {
          setEmployeeList((prev) =>
            prev.some((e) => e.code === employeeCode)
              ? prev
              : [...prev, { code: employeeCode, name: employeeName }],
          );
        }

        onSaveEntry && onSaveEntry(key, newRecord);
        closeModal();
      } else {
        toast.error(item?.message || "Please try again !!", {
          duration: 4000,
          position: "top-right",
        });
      }
      setDisabled(false);
    });
  };
  useEffect(() => {
    if (!modalDate && !listModalDate) return;
    function onKey(e) {
      if (e.key === "Escape") {
        closeModal();
        closeListModal();
      }
    }
    document.addEventListener("keydown", onKey);
    if (modalDate) firstFieldRef.current && firstFieldRef.current.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [modalDate, listModalDate]);

  // function renderChip(entry) {
  //   if (!entry) return null;
  //   const meta = STATUS_META[entry.status];
  //   if (!meta) return null;
  //   return (
  //     <span
  //       className="atc-chip"
  //       style={{
  //         color: meta.dot,
  //         background: "color-mix(in srgb, " + meta.dot + " 14%, transparent)",
  //       }}
  //     >
  //       <span className="atc-dot" style={{ background: meta.dot }} />
  //       {meta.label}
  //     </span>
  //   );
  // }
  function normalizeStatus(status) {
  if (!status) return "";

  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}
  function renderChip(entry) {
  if (!entry) return null;

  const status = normalizeStatus(entry.status);
  const meta = STATUS_META[status];

  if (!meta) return null;

  return (
    <span
      className="atc-chip"
      style={{
        color: meta.dot,
        background:
          "color-mix(in srgb, " + meta.dot + " 14%, transparent)",
      }}
    >
      <span
        className="atc-dot"
        style={{ background: meta.dot }}
      />
      {meta.label}
    </span>
  );
}

  function renderCellContent(entry) {
    if (isMultiEmployeeView && Array.isArray(entry)) {
      const visible = entry.slice(0, 2);
      const remaining = entry.length - visible.length;
      return (
        <>
          {visible.map((e, idx) => (
            <span
              key={idx}
              className="atc-chip"
              style={{
                color: STATUS_META[e.status]?.dot,
                background:
                  "color-mix(in srgb, " +
                  STATUS_META[e.status]?.dot +
                  " 14%, transparent)",
              }}
            >
              <span
                className="atc-dot"
                style={{ background: STATUS_META[e.status]?.dot }}
              />
              {e.employeeName || e.employeeCode}
            </span>
          ))}
          {remaining > 0 && (
            <span
              className="atc-weekend-label atc-more-link"
              onClick={(ev) => {
                ev.stopPropagation();
                openListModal(
                  new Date(
                    entry[0].attendanceDate
                      ? entry[0].attendanceDate + "T00:00:00"
                      : selected,
                  ),
                );
              }}
            >
              +{remaining} more
            </span>
          )}
        </>
      );
    }
    return renderChip(entry);
  }

  return (
    <div className="atc">
      <Layout />
      <header className="atc-topbar">
        <div className="atc-brand">
          <span className="atc-brand-mark" aria-hidden="true">
            A
          </span>
          <div>
            <h1>Attendance</h1>
            <p className="atc-brand-sub">
              {isAdmin
                ? selectedEmployeeCode
                  ? employeeList.find((e) => e.code === selectedEmployeeCode)
                      ?.name || selectedEmployeeCode
                  : "All Employees"
                : loggedInEmployeeName}
            </p>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {isAdmin && (
            <select
              className="atc-btn atc-btn-ghost"
              value={selectedEmployeeCode}
              onChange={(e) => setSelectedEmployeeCode(e.target.value)}
              aria-label="Select employee"
            >
              <option value="">All Employees</option>
              {employeeList.map((emp) => (
                <option key={emp.code} value={emp.code}>
                  {emp.name}
                </option>
              ))}
            </select>
          )}
          {!isMultiEmployeeView && (
            <button
              className="atc-btn atc-btn-primary"
              onClick={() => openModalFor(new Date())}
            >
              <span className="atc-plus">+</span> Mark Today
            </button>
          )}
        </div>
      </header>

      <div className="atc-body">
        <aside className="atc-sidebar">
          <div className="atc-card atc-mini">
            <div className="atc-mini-head">
              <span>{monthLabel}</span>
              <div className="atc-mini-nav">
                <button
                  aria-label="Previous month"
                  onClick={() => stepMonth(-1)}
                >
                  ‹
                </button>
                <button aria-label="Next month" onClick={() => stepMonth(1)}>
                  ›
                </button>
              </div>
            </div>
            <div className="atc-mini-dow">
              {WEEKDAYS_FULL.map((d) => (
                <span key={d}>{d[0]}</span>
              ))}
            </div>
            <div className="atc-mini-grid">
              {miniGrid.flat().map((date, i) => {
                const inMonth = date.getMonth() === cursor.getMonth();
                const entry = entryFor(date);
                const isToday = isSameDay(date, today);
                const isSelected = isSameDay(date, selected);
                const dotEntry = Array.isArray(entry) ? entry[0] : entry;
                return (
                  <button
                    key={i}
                    className={[
                      "atc-mini-day",
                      !inMonth && "is-outside",
                      isToday && "is-today",
                      isSelected && "is-selected",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => openModalFor(date)}
                  >
                    {date.getDate()}
                    {dotEntry && (
                      <span
                        className="atc-mini-dot"
                        style={{
                          background: STATUS_META[dotEntry.status]?.dot,
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="atc-card atc-legend">
            <h2>Status</h2>
            <ul>
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <li key={key}>
                  <span className="atc-dot" style={{ background: meta.dot }} />
                  {meta.label}
                </li>
              ))}
            </ul>
          </div>

          <div className="atc-card atc-stats">
            <h2>{monthLabel} Summary</h2>
            <div className="atc-stat-grid">
              <div className="atc-stat">
                <span
                  className="atc-stat-num"
                  style={{ color: "var(--present)" }}
                >
                  {stats.Present}
                </span>
                <span className="atc-stat-label">Present</span>
              </div>
              <div className="atc-stat">
                <span
                  className="atc-stat-num"
                  style={{ color: "var(--absent)" }}
                >
                  {stats.absent}
                </span>
                <span className="atc-stat-label">Absent</span>
              </div>
              <div className="atc-stat">
                <span className="atc-stat-num" style={{ color: "var(--late)" }}>
                  {stats.late}
                </span>
                <span className="atc-stat-label">Late</span>
              </div>
              <div className="atc-stat">
                <span
                  className="atc-stat-num"
                  style={{ color: "var(--leave)" }}
                >
                  {stats.leave}
                </span>
                <span className="atc-stat-label">Leave</span>
              </div>
            </div>
          </div>
        </aside>

        <main className="atc-main">
          <div className="atc-toolbar">
            <div className="atc-toolbar-left">
              <button
                className="atc-icon-btn"
                aria-label="Previous"
                onClick={handlePrev}
              >
                ‹
              </button>
              <button className="atc-btn atc-btn-ghost" onClick={goToday}>
                Today
              </button>
              <button
                className="atc-icon-btn"
                aria-label="Next"
                onClick={handleNext}
              >
                ›
              </button>
              <h2 className="atc-toolbar-title">{headerDateLabel}</h2>
            </div>
            <div className="atc-view-switch" role="tablist">
              {["day", "week", "month"].map((v) => (
                <button
                  key={v}
                  role="tab"
                  aria-selected={view === v}
                  className={"atc-view-btn" + (view === v ? " is-active" : "")}
                  onClick={() => setView(v)}
                >
                  {v[0].toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="atc-view-area">
            {loading && (
              <p style={{ padding: "16px 20px", color: "var(--text-muted)" }}>
                Loading attendance…
              </p>
            )}
            {error && (
              <p style={{ padding: "16px 20px", color: "var(--absent)" }}>
                {error}
              </p>
            )}

            {!loading && view === "month" && (
              <>
                <div className="atc-grid-head">
                  {WEEKDAYS_FULL.map((d) => (
                    <div key={d}>{d}</div>
                  ))}
                </div>
                <div className="atc-month-grid">
                  {monthGrid.flat().map((date, i) => {
                    const inMonth = date.getMonth() === cursor.getMonth();
                    const entry = entryFor(date);
                    const isWeekend =
                      date.getDay() === 0 || date.getDay() === 6;
                    const isToday = isSameDay(date, today);
                    const locked = !isMultiEmployeeView && !canEditDate(date);
                    return (
                      <button
                        key={i}
                        className={[
                          "atc-cell",
                          !inMonth && "is-outside",
                          isWeekend && "is-weekend",
                          isToday && "is-today",
                          locked && "is-locked",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => openModalFor(date)}
                        title={
                          locked
                            ? "Only admin can edit previous days"
                            : undefined
                        }
                      >
                        <span className="atc-cell-num">{date.getDate()}</span>
                        {renderCellContent(entry)}
                        {isWeekend && !entry && (
                          <span className="atc-weekend-label">Weekend</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {!loading && view === "week" && (
              <div className="atc-week-grid">
                {weekGrid.map((date, i) => {
                  const entry = entryFor(date);
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  const isToday = isSameDay(date, today);
                  const locked = !isMultiEmployeeView && !canEditDate(date);
                  return (
                    <button
                      key={i}
                      className={[
                        "atc-week-cell",
                        isWeekend && "is-weekend",
                        isToday && "is-today",
                        locked && "is-locked",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => openModalFor(date)}
                      title={
                        locked ? "Only admin can edit previous days" : undefined
                      }
                    >
                      <span className="atc-week-dow">
                        {WEEKDAYS_FULL[date.getDay()]}
                      </span>
                      <span className="atc-week-num">{date.getDate()}</span>
                      {entry ? (
                        renderCellContent(entry)
                      ) : isWeekend ? (
                        <span className="atc-weekend-label">Weekend</span>
                      ) : (
                        <span className="atc-weekend-label atc-empty">
                          No record
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {!loading && view === "day" && (
              <div className="atc-day-panel">
                <div className="atc-day-hero">
                  <span className="atc-day-date">{selected.getDate()}</span>
                  <div>
                    <p className="atc-day-dow">
                      {WEEKDAYS_FULL[selected.getDay()]}
                    </p>
                    <p className="atc-day-month">
                      {MONTH_NAMES[selected.getMonth()]}{" "}
                      {selected.getFullYear()}
                    </p>
                  </div>
                </div>
                {(() => {
                  const entry = entryFor(selected);
                  const isWeekend =
                    selected.getDay() === 0 || selected.getDay() === 6;

                  if (
                    isMultiEmployeeView &&
                    Array.isArray(entry) &&
                    entry.length
                  ) {
                    return (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          width: "100%",
                          maxWidth: "420px",
                        }}
                      >
                        {entry.map((e, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="atc-list-row"
                            onClick={() =>
                              openEditFromList(selected, e.employeeCode)
                            }
                          >
                            <span className="atc-list-name">
                              {e.employeeName || e.employeeCode}
                            </span>
                            {renderChip(e)}
                          </button>
                        ))}
                      </div>
                    );
                  }

                  if (entry && !Array.isArray(entry)) {
                    return (
                      <>
                        <div
                          className="atc-chip atc-chip-lg"
                          style={{
                            color: STATUS_META[entry.status]?.dot,
                            background:
                              "color-mix(in srgb, " +
                              STATUS_META[entry.status]?.dot +
                              " 14%, transparent)",
                          }}
                        >
                          <span
                            className="atc-dot"
                            style={{
                              background: STATUS_META[entry.status]?.dot,
                            }}
                          />
                          {STATUS_META[entry.status]?.label}
                        </div>
                        {(entry.startTime || entry.endTime) && (
                          <p className="atc-day-time">
                            {entry.startTime || "—"} – {entry.endTime || "—"}
                          </p>
                        )}
                        {entry.notes && (
                          <p className="atc-day-notes">{entry.notes}</p>
                        )}
                      </>
                    );
                  }
                  return (
                    <div className="atc-weekend-label atc-empty">
                      {isWeekend
                        ? "Weekend — no record required"
                        : "No attendance recorded"}
                    </div>
                  );
                })()}
                {!isMultiEmployeeView && canEditDate(selected) && (
                  <button
                    className="atc-btn atc-btn-primary"
                    onClick={() => openModalFor(selected)}
                  >
                    {entryFor(selected) ? "Edit entry" : "Add entry"}
                  </button>
                )}
                {!isMultiEmployeeView && !canEditDate(selected) && (
                  <p className="atc-weekend-label atc-empty">
                    Only admin can add or edit a previous day
                  </p>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ---------- "View all" list modal (admin, multi-employee day) ---------- */}
      {listModalDate && (
        <div className="atc-modal-backdrop" onMouseDown={closeListModal}>
          <div
            className="atc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="atc-list-modal-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="atc-modal-head">
              <div>
                <h3 id="atc-list-modal-title">
                  {WEEKDAYS_FULL[listModalDate.getDay()]},{" "}
                  {MONTH_NAMES[listModalDate.getMonth()]}{" "}
                  {listModalDate.getDate()}, {listModalDate.getFullYear()}
                </h3>
                <p>All employee attendance</p>
              </div>
              <button
                className="atc-icon-btn"
                aria-label="Close"
                onClick={closeListModal}
              >
                ×
              </button>
            </div>
            <div className="atc-modal-list-body">
              {(entries[toKey(listModalDate)] || []).length === 0 && (
                <p className="atc-weekend-label atc-empty">
                  No attendance recorded
                </p>
              )}
              {(entries[toKey(listModalDate)] || []).map((e, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="atc-list-row"
                  onClick={() =>
                    openEditFromList(listModalDate, e.employeeCode)
                  }
                >
                  <span className="atc-list-name">
                    {e.employeeName || e.employeeCode}
                  </span>
                  {renderChip(e)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ---------- Create / edit entry modal ---------- */}
      {modalDate && (
        <div className="atc-modal-backdrop" onMouseDown={closeModal}>
          <div
            className="atc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="atc-modal-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="atc-modal-head">
              <div>
                <h3 id="atc-modal-title">
                  {WEEKDAYS_FULL[modalDate.getDay()]},{" "}
                  {MONTH_NAMES[modalDate.getMonth()]} {modalDate.getDate()},{" "}
                  {modalDate.getFullYear()}
                </h3>
                <p>
                  {isAdmin
                    ? employeeList.find((e) => e.code === modalEmployeeCode)
                        ?.name || modalEmployeeCode
                    : loggedInEmployeeName}
                </p>
              </div>
              <button
                className="atc-icon-btn"
                aria-label="Close"
                onClick={closeModal}
              >
                ×
              </button>
            </div>
            <form className="atc-modal-form" onSubmit={handleSave}>
              <label className="atc-field">
                <span>Status</span>
                <div className="atc-status-picker">
                  {Object.entries(STATUS_META).map(([key, meta]) => (
                    <button
                      type="button"
                      key={key}
                      className={
                        "atc-status-option" +
                        (form.status === key ? " is-active" : "")
                      }
                      style={
                        form.status === key
                          ? {
                              borderColor: meta.dot,
                              color: meta.dot,
                              background:
                                "color-mix(in srgb, " +
                                meta.dot +
                                " 12%, transparent)",
                            }
                          : undefined
                      }
                      onClick={() => setForm((f) => ({ ...f, status: key }))}
                    >
                      <span
                        className="atc-dot"
                        style={{ background: meta.dot }}
                      />
                      {meta.label}
                    </button>
                  ))}
                </div>
              </label>

              <div className="atc-field-row">
                <label className="atc-field">
                  <span>Start time</span>
                  <input
                    ref={firstFieldRef}
                    type="time"
                    value={form.startTime}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, startTime: e.target.value }))
                    }
                  />
                </label>
                <label className="atc-field">
                  <span>End time</span>
                  <input
                    type="time"
                    value={form.endTime}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, endTime: e.target.value }))
                    }
                  />
                </label>
              </div>

              <label className="atc-field">
                <span>Notes</span>
                <textarea
                  rows={3}
                  placeholder="Task, project, or reason (optional)"
                  value={form.notes}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, notes: e.target.value }))
                  }
                />
              </label>

              <div className="atc-modal-actions">
                <button
                  type="button"
                  className="atc-btn atc-btn-ghost"
                  onClick={closeModal}
                  disabled={disabled}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="atc-btn atc-btn-primary"
                  disabled={disabled}
                >
                  Save entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
